import React from 'react';
import { observer } from 'mobx-react-lite';
import TableContextProvider, { useTableControls } from 'common/components/table/table-context';
import { Card, Col, Row, Space } from 'antd';
import { SearchFilter } from 'common/components/table/filters/search-filter/search-filter';
import { FilterTypesEnum } from 'common/enums/filter-types.enum';
import vehicleService from 'common/services/vehicle-service/vehicle.service';
import { vehicleAdminInitialColumns } from '../../vehicles.config';
import TableBase from 'common/components/table/table-base';
import { ColumnVisibility } from 'common/components/table/filters/column-visibility/column-visibility';
import featureStore from 'common/stores/features/feature.store';
import {
  filterColumnsByPersonalization,
  getTablePersonalizationColumnsData,
  getTablePersonalizationSortData,
  personalizationDefaultTaleSort,
} from 'common/components/table/table-helpers';
import userPersonalizationStore from 'common/stores/user-personalization/user-personalization.store';
import { getVehicleRowClassName } from 'common/helpers/vehicles.helper';

const TABLE_NAME = 'all-vehicles';

export const AllVehicles = observer(() => {
  let columns = vehicleAdminInitialColumns;

  if (!featureStore.isCardAccessEnabled) {
    columns = columns.filter((i) => i.key !== 'isNoTransponder');
  }

  if (!featureStore.isCardAccessClassesEnabled) {
    columns = columns.filter((i) => i.key !== 'cardClass');
  }

  if (!featureStore.isCardAccessExpirationsEnabled) {
    columns = columns.filter((i) => i.key !== 'validityPeriod');
  }

  if (!featureStore.isSmartPlateIdEnabled) {
    columns = columns.filter((i) => i.key !== 'smartPlateId');
  }

  const getDefaultSort = () => {
    return (
      personalizationDefaultTaleSort(TABLE_NAME) || {
        key: 'owner',
        order: 'asc',
      }
    );
  };

  const tableControls = useTableControls({
    onFetch: vehicleService.getVehicles,
    initialColumns: filterColumnsByPersonalization(columns, TABLE_NAME),
    defaultSort: getDefaultSort(),
    onUpdateColumns: async (columns: any[], sortBy: any) => {
      await userPersonalizationStore.updateTableColumnsPersonalization({
        tableName: TABLE_NAME,
        tableColumns: getTablePersonalizationColumnsData(columns),
        tableSort: getTablePersonalizationSortData(sortBy),
      });
    },
  });

  return (
    <>
      <TableContextProvider controls={tableControls}>
        <Card className="eiq-card eiq-card-border full-card">
          <Row className="eiq-card-header">
            <Col span={24}>
              <h1 className="h1">Vehicles</h1>
            </Col>
          </Row>
          <Row gutter={8} align="middle" style={{ marginBottom: '16px' }}>
            <Col flex="auto">
              <SearchFilter
                placeholder={`Search by owner name, resident name, license plate${
                  featureStore.isSmartPlateIdEnabled ? ', smartPlate ID' : ''
                } or transponder ID`}
                rulesForColumn={{
                  owner: FilterTypesEnum.Contains,
                  licenseNumber: FilterTypesEnum.Contains,
                  ...(featureStore.isSmartPlateIdEnabled && { smartPlateId: FilterTypesEnum.Contains }),
                  deviceId: FilterTypesEnum.StartWith,
                  address: FilterTypesEnum.StartWith,
                  residentName: FilterTypesEnum.Contains,
                }}
              />
            </Col>
            <Col>
              <Space size={8}>
                <ColumnVisibility isColumnReorderAllow={true} />
              </Space>
            </Col>
          </Row>
          <TableBase onSelected={tableControls.setSelectedRow} selectFirstRowByDefault rowClassName={getVehicleRowClassName} />
        </Card>
      </TableContextProvider>
    </>
  );
});
